import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesSentosaButlerPackagePage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Raffles Sentosa Singapore「The Butler Did It」パッケージ' }
  ];

  const article = {
    category: '高付加価値サービス・価格設定',
    date: '2025年10月15日',
    title: 'Raffles Sentosa Singapore「The Butler Did It」パッケージ',
    subtitle: '1泊約55万円の超高額ラグジュアリー体験',
    heroImage: '/images/generated/jba-fs-117-news_raffles-sentosa-butler-package.webp',
    lead: 'Raffles Sentosa Singaporeが発表した「The Butler Did It」4日間3泊のビスポークラグジュアリーパッケージは、1泊約55万円という超高額設定で、専属バトラーサービスを中核とした究極のパーソナライゼーション体験を提供します。ウェルビーイングバトラーという専門化された新しいサービス形態も注目を集めています。',
    comment: 'Raffles Sentosa Singaporeの「The Butler Did It」パッケージは、執事サービスが超高額ラグジュアリー体験の中核的価値として認識されていることを明確に示す画期的な事例として、弊協会としても深く注目しております。特に「ウェルビーイングバトラー」という専門化の方向性は、執事という職業の進化と可能性を示すものです。\n\n1泊約55万円という価格設定において、専属バトラーサービスが不可欠な要素として組み込まれていることは、パーソナライゼーションの極致としての執事の価値を世界に示しています。\n\nこのような国際的な高付加価値サービスの展開は、日本の富裕層市場における同様のサービス開発の可能性を示唆しており、私どもとしても、心身の健康と幸福を総合的にサポートする次世代の執事サービスの確立に向けて、積極的に取り組んでまいります。',
    related: [
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-122-news_raffles-sentosa-butler-package-v4.webp', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」グローバルキャンペーンの関連記事', title: 'Raffles「The Butler Did It」グローバルキャンペーン', desc: 'ラグジュアリーホテルの執事サービス革新' },
      { to: '/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-123-news_raffles-sentosa-butler-package-v4.webp', aspect: 'aspect-[4/3]', alt: 'AIホスピタリティ革命：5つの変革の関連記事', title: 'AIホスピタリティ革命：5つの変革', desc: '人工知能が切り開く執事サービスの未来' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'TTG Asia', link: false },
    { label: 'パッケージ報道', value: 'https://www.ttgasia.com/2025/10/15/raffles-sentosa-singapore-unveils-bespoke-four-day-luxury-escape/', link: true },
    { label: 'オールヴィラリゾート発表', value: 'https://www.ttgasia.com/2025/05/19/raffles-sentosa-singapore-unveils-first-ever-all-villa-resort/', link: true },
    { label: 'リゾート開業データ', value: 'https://press.accor.com/raffles-sentosa-singapore-debuts-a-glamorous-resort-experience-in-one-of-the-worlds-most-dynamic-destinations', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-pricing', label: 'パッケージ概要と価格設定' },
    { id: 's-resort', label: 'オールヴィラリゾートとしての開業' },
    { id: 's-dining', label: 'キュレーテッドダイニング体験' },
    { id: 's-bespoke', label: 'ビスポーク体験プログラム' },
    { id: 's-lineup', label: 'リゾートの構成と5つのダイニング' },
    { id: 's-wellbeing', label: 'ウェルビーイングバトラーサービス' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Luxury" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '「The Butler Did It」は4日間3泊のビスポーク企画。ガーデン1ベッドルームプールヴィラが対象で、1泊SG$4,985（約55万円）から（別途サービスチャージ・税）',
                '舞台のRaffles Sentosa Singaporeは2025年3月1日開業のブランド初オールヴィラリゾート。62棟のプールヴィラを約10万㎡の敷地に配置する',
                '専属バトラーに加え、リゾートのウェルネスチームと連携する「ウェルビーイングバトラー」が新しいサービス形態として注目を集めている'
              ]}
            />

            {/* 01: 概要と価格設定（既存本文・一字も削らず） */}
            <SectionHeading id="s-pricing" en="Pricing">パッケージ概要と価格設定</SectionHeading>
            <div className="space-y-6">
              <p className={P}>「The Butler Did It」パッケージは、ガーデン1ベッドルームプールヴィラを対象とし、1泊4,985シンガポールドル（約55万円）からという超高額設定となっています。この価格には別途サービスチャージと税金が加算されます。</p>
              <p className={P}>ゲストはプライベートリムジンで迎えられ、滞在期間中は専属のRafflesバトラーが付き添います。ヴィラにはプライベートプール、ガーデンテラス、広々としたリビングエリアが完備され、完全にプライベートな空間での滞在が保証されています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-sentosa-butler-package-ttgasia.jpg"
              alt="TTG Asiaに掲載されたRaffles Sentosa Singaporeのビスポーク4日間ラグジュアリーパッケージ報道記事のスクリーンショット"
              media="TTG Asia"
              url="https://www.ttgasia.com/2025/10/15/raffles-sentosa-singapore-unveils-bespoke-four-day-luxury-escape/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="専属バトラーを中核に、1泊約55万円という超高額の究極のパーソナライゼーション体験。" cite="Raffles Sentosa Singapore「The Butler Did It」" />
            </div>

            {/* 増補: オールヴィラリゾート */}
            <SectionHeading id="s-resort" en="All-Villa Resort">オールヴィラリゾートとしての開業</SectionHeading>
            <div className="space-y-6">
              <p className={P}>本パッケージの舞台となるRaffles Sentosa Singaporeは、<Stat>2025年3月1日</Stat>に開業した、ブランド初のオールヴィラリゾートです。約<Stat>10万㎡</Stat>の敷地に<Stat>62棟</Stat>のプライベートプールヴィラを配置し、都市型リゾートとは一線を画す静謐で私邸のような滞在を実現しています。</p>
              <p className={P}>全室がヴィラという構成そのものが、専属バトラーによる完全なプライバシーとパーソナライズドサービスの基盤となっており、本パッケージはこのリゾートの特性を最大限に活かした企画として位置づけられています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-sentosa-butler-package-ttgasia2.jpg"
              alt="TTG AsiaのRaffles Sentosa Singaporeがブランド初のオールヴィラリゾートとして発表されたことを報じる記事のスクリーンショット"
              media="TTG Asia"
              url="https://www.ttgasia.com/2025/05/19/raffles-sentosa-singapore-unveils-first-ever-all-villa-resort/"
            />

            {/* 02: ダイニング（既存本文・一字も削らず） */}
            <SectionHeading id="s-dining" en="Dining">キュレーテッドダイニング体験</SectionHeading>
            <div className="space-y-6">
              <p className={P}>朝食はヴィラ内またはモダンイタリアンレストラン「Empire Grill」で提供され、キュレーテッドテイスティングメニューには、Empire Grillのモダンイタリア料理と、エグゼクティブシェフLing Heng Yaoによる「Royal China」の広東料理が含まれています。</p>
              <p className={P}>これらのダイニング体験は、単なる食事提供を超えて、ゲストの好みと文化的背景を深く理解した上でカスタマイズされた美食の旅として設計されています。</p>
            </div>

            {/* 03: ビスポーク体験（既存本文・一字も削らず） */}
            <SectionHeading id="s-bespoke" en="Bespoke">ビスポーク体験プログラム</SectionHeading>
            <div className="space-y-6">
              <p className={P}>体験プログラムには、プライベートロールスロイスツアーでセントーサ島内を巡る贅沢な移動体験、シグネチャーカクテル「Raffles Sentosa Sling」のテイスティングセッション、そして個人の好みに合わせたビスポークの香水を調合するフレグランスメイキングワークショップが含まれています。</p>
              <p className={P}>滞在の締めくくりとして、Sisters' Islands（シスターズ諸島）へのプライベートヨットチャーターが提供され、サンセット時にシャンパンとグルメカナッペを楽しむ究極のフィナーレ体験が用意されています。</p>
            </div>

            {/* 増補: リゾートの構成と5つのダイニング */}
            <SectionHeading id="s-lineup" en="Resort Lineup">リゾートの構成と5つのダイニング</SectionHeading>
            <div className="space-y-6">
              <p className={P}>リゾートには、パッケージに登場するEmpire GrillとRoyal Chinaを含め、合計<Stat>5</Stat>つのレストラン＆ラウンジが設けられています。日本料理の「Iyasaka by Hashida」、「Raffles Room」、「Chairman's Room」がラインナップに加わり、多彩な美食体験を可能にしています。</p>
              <p className={P}>広大な敷地に点在するこれらの施設を、ゲストの好みに応じて専属バトラーがシームレスに手配することで、パッケージの価値がさらに高められています。</p>
            </div>

            {/* 04: ウェルビーイングバトラー（既存本文・一字も削らず） */}
            <SectionHeading id="s-wellbeing" en="Wellbeing Butler">ウェルビーイングバトラーサービス</SectionHeading>
            <div className="space-y-6">
              <p className={P}>特に注目すべきは、専任のウェルビーイングバトラーがリゾートのウェルネスチームと連携して提供するサービスです。これには、テーラーメイドのスパトリートメント、栄養ガイダンス、マインドフルネス実践が含まれています。</p>
              <p className={P}>このウェルビーイングバトラーという概念は、従来の執事サービスの専門化を示す新しい方向性として、業界内で大きな注目を集めています。単なるサービス提供を超えて、ゲストの心身の健康と幸福を総合的にサポートする専門家としての役割を担っています。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">リゾート開業</dt><dd className="text-[#333] mt-0.5">2025年3月1日</dd></div>
                  <div><dt className="text-[#8f7a56]">ヴィラ数</dt><dd className="text-[#333] mt-0.5">62棟（オールヴィラ）</dd></div>
                  <div><dt className="text-[#8f7a56]">パッケージ料金</dt><dd className="text-[#333] mt-0.5">1泊SG$4,985〜</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
